import ShopifyVariantsUI from '@pixelunion/shopify-variants-ui';
import ShopifySurfacePickUp from '@pixelunion/shopify-surface-pick-up';
import EventHandler from '@pixelunion/events';

import QuantitySelector from '../helpers/QuantitySelector';

export default class ProductDetails {
  constructor(options) {
    this.el = options.el;
    this.product = options.product;
    this.variantInventory = options.variantInventory;
    this.settings = options.settings;
    this.onVariantChange = options.onVariantChange;
    this.selectFirstAvailableVariant = this.settings.selectFirstAvailableVariant;
    this.soldOutOptions = this.settings.soldOutOptions;
    this.productDetailsContainer = this.el.querySelector('[data-product-details]');
    this.variantsUIElement = this.el.querySelector('[data-variants-ui]');
    this.productUnitPricing = this.el.querySelector('[data-unit-price]');
    this.sizeChartButton = this.el.querySelector('[data-size-chart-trigger]');
    this.sizeChartContent = this.el.querySelector('[data-size-chart]');

    this.surfacePickUpEl = this.el.querySelector('[data-surface-pick-up]'); // Element to inject surface pick up information into on product
    this.surfacePickUp = new ShopifySurfacePickUp(this.surfacePickUpEl);

    this.events = new EventHandler();

    // Set up variants ui
    this.variantsUI = new ShopifyVariantsUI(
      this.variantsUIElement,
      this.product,
      {
        selectFirstAvailableVariant: this.selectFirstAvailableVariant,
        disableUnavailableOptions: this.soldOutOptions === 'disabled',
        removeUnavailableOptions: this.soldOutOptions === 'hidden',
      },
    );

    this.surfacePickUp.load(this.variantsUI.currentVariant.id);
    this.surfacePickUp.onModalRequest(contents => {
      const container = document.createElement('div');
      let variantTitle;
      let headerClass;

      if (!this.variantsUI.isDefaultVariant) {
        variantTitle = `<div class="surface-pick-up-modal__variant">${this.variantsUI.currentVariant.title}</div>`;
      } else {
        variantTitle = '';
        headerClass = 'surface-pick-up-modal__header--no-variants';
      }



      const modalContents = `
        <div class="surface-pick-up-modal__header ${headerClass}">
          <h2 class="surface-pick-up-modal__title">${this.product.title}</h2>
          ${variantTitle}
        </div>
        ${contents}
        `;

      container.innerHTML = modalContents;

      window.modal.open({
        trigger: document.querySelector('[data-surface-pick-up-embed-modal-btn]'),
        classname: 'surface-pick-up__popup',
        content: container,
      });
    });

    this.events.register(this.sizeChartButton, 'click', e => {
      e.preventDefault();

      window.modal.open({
        trigger: this.sizeChartButton,
        classname: 'product__size-chart-modal',
        content: this.sizeChartContent,
      });

      this.sizeChartContent.dataset.enabled = true;
    });

    // Form
    this.formArea = this.el.querySelector('[data-product-form-area]');
    this.productVariants = this.el.querySelector('[data-variants]');

    this.backInStockForm = this.el.querySelector('[data-back-in-stock-form]');
    this.productFormToggles = this.el.querySelectorAll('[data-selected-variant]');
    this.inventoryRemainingContainer = this.el.querySelector('[data-inventory-remaining]');
    this.inventoryRemainingText = this.el.querySelector('[data-inventory-remaining-count]');
    this.quantityBox = this.el.querySelector('[data-product-quantity-box]');
    this.oneLeft = Shopify.translation.items_left_count_one;
    this.moreLeft = Shopify.translation.items_left_count_other;

    this.variantFields = {
      atcContainer: this.el.querySelector('[data-atc-wrapper]'),
      unavailableText: this.el.querySelector('[data-unavailable-text]'),
      priceContainer: this.el.querySelector('[data-price-container]'),
      price: this.el.querySelector('[data-product-price]'),
      priceCompare: this.el.querySelector('[data-product-price-compare]'),
      atcButton: this.el.querySelector('[data-product-atc]'),
      atcButtonText: this.el.querySelector('[data-product-atc-text]'),
      sku: this.el.querySelector('[data-product-sku]'),
      unitPrice: this.el.querySelector('[data-unit-price]'),
      totalQuantity: this.el.querySelector('[data-total-quantity]'),
      unitPriceAmount: this.el.querySelector('[data-unit-price-amount]'),
      unitPriceMeasure: this.el.querySelector('[data-unit-price-measure]'),
    };

    // Update featured image on page load
    this.onVariantChange(this.variantsUI.currentVariant);

    if (this.quantityBox) {
      this.productQuantityBox = new QuantitySelector({
        quantityField: this.quantityBox,
      });
    }

    this.variantsUI.onVariantChange(data => this._onSwitchVariant(data));

    // Checks if all variant options have been selected
    this.events.register(this.variantFields.atcButton, 'click', e => {
      e.preventDefault();
      if (!this.variantsUI.currentVariant) {
        this.formArea.classList.add('product-form--error-option-unselected');
        this.productDetailsContainer.classList.add('product-form--error-option-unselected');
      } else {
        this.formArea.classList.remove('product-form--error-option-unselected');
        this.productDetailsContainer.classList.remove('product-form--error-option-unselected');
        if (Shopify.theme_settings.cart_action_type !== 'ajax') {
          this.formArea.querySelector('[data-product-form]').submit();
        }
      }
    });
  }

  unload() {
    if (this.productQuanityBox) {
      this.productQuantityBox.unload();
    }

    this.surfacePickUp.unload();
    this.variantsUI.unload();
    this.events.unregisterAll();
  }

  _updatePrice(variant) {
    const {
      unavailableText,
      priceContainer,
      price,
      priceCompare,
      atcButton,
      atcButtonText,
      atcContainer,
      unitPrice,
      totalQuantity,
      unitPriceAmount,
      unitPriceMeasure,
    } = this.variantFields;

    const { moneyFormat } = document.body.dataset;

    this.formatMoney = Shopify.currencyHelper.formatMoney;

    if (variant.unit_price && variant.available) {
      this.productUnitPricing.classList.remove('product__unit-price--hidden');
      unitPriceAmount.innerHTML = this.formatMoney(variant.unit_price, moneyFormat);
      unitPriceMeasure.innerHTML = variant.unit_price_measurement.quantity_unit;
      totalQuantity.innerHTML = `${variant.unit_price_measurement.quantity_value}${variant.unit_price_measurement.quantity_unit}`;
    } else {
      this.productUnitPricing.classList.add('product__unit-price--hidden');
    }

    const variantPrice = this.formatMoney(variant.price, moneyFormat);
    const variantComparePrice = this.formatMoney(variant.compare_at_price, moneyFormat);

    // Display free price text for $0 products
    if (variant.price > 0) {
      price.innerHTML = variantPrice;
    } else if (variant.available) {
      price.innerHTML = Shopify.theme_settings.free_text;
    } else {
      price.innerHTML = '';
    }

    // Sale scenario
    if (variant.price < variant.compare_at_price) {
      priceContainer.dataset.sale = true;
      priceContainer.dataset.comparePriceVisible = true;
      priceCompare.innerHTML = variantComparePrice;
    } else {
      priceContainer.dataset.sale = false;
      priceContainer.dataset.comparePriceVisible = false;
      priceCompare.innerHTML = '';
    }

    if (atcContainer.dataset.selectedVariant === 'false') {
      atcContainer.dataset.selectedVariant = 'true';
    }

    if (this.productFormToggles) {
      this.productFormToggles.forEach(option => {
        const toggle = option;
        if (toggle.dataset.selectedVariant === 'false') {
          toggle.dataset.selectedVariant = 'true';
        }
      });
    }

    // Checking if variant is available to purchase
    if (!variant) {
      // Unavailable
      atcButtonText.innerText = Shopify.translation.product_unavailable;
      atcButton.disabled = true;
      priceContainer.dataset.unavailable = true;
      unavailableText.innerText = Shopify.translation.product_unavailable;
      if (this.backInStockForm) {
        this.backInStockForm.dataset.formEnabled = false;
      }
    } else if (variant.available) {
      // Available and not sold out
      atcButtonText.innerText = Shopify.translation.product_add_to_cart;
      atcButton.disabled = false;
      priceContainer.dataset.soldOut = false;
      priceContainer.dataset.unavailable = false;
      if (this.backInStockForm) {
        this.backInStockForm.dataset.formEnabled = false;
      }
    } else {
      // Sold out
      atcButtonText.innerText = Shopify.translation.product_sold_out;
      atcButton.disabled = true;
      priceContainer.dataset.soldOut = true;
      priceContainer.dataset.unavailable = false;

      // If show back in stock form enabled, show form
      if (Shopify.theme_settings.notify_me_form) {
        this.backInStockForm.dataset.formEnabled = true;
      }
    }
  }

  _updateSKU(variant) {
    if (!this.variantFields.sku) { return; }
    if (variant.sku === '') {
      this.variantFields.sku.classList.add('product__sku--empty');
    } else if (variant.sku) {
      this.variantFields.sku.classList.remove('product__sku--empty');
      this.variantFields.sku.innerHTML = variant.sku;
    }
  }

  _updateInventoryRemaining({ available, id }) {
    if (available === undefined) {
      return;
    }
    if (!this.variantInventory) { return; }

    // These are taken from the admin settings for the variant
    const {
      inventory_quantity,
      inventory_policy,
    } = this.variantInventory[id];

    // Method for updating the inventory remaining text
    const updateInventoryRemainingText = () => {
      if (!this.inventoryRemainingContainer) { return; }
      if (!this.inventoryRemainingText) { return; }
      if (!available) {
        this.inventoryRemainingContainer.dataset.enabled = false;
        return;
      }

      // Only show remaining inventory if less than or equal to threshold amount
      if (inventory_quantity <= Shopify.theme_settings.inventory_threshold) {
        this.newInventoryQuantity = inventory_quantity;
        this.itemsLeftText = this.newInventoryQuantity === 1 ? this.oneLeft : this.moreLeft;
        this.inventoryRemainingContainer.dataset.enabled = true;
        this.inventoryRemainingText.innerText = `${this.newInventoryQuantity} ${this.itemsLeftText}`;
      } else {
        this.inventoryRemainingContainer.dataset.enabled = false;
      }
    };

    // Method for updating the quantity box
    const updateQuantityBox = () => {
      if (!this.quantityBox) { return; }
      if (!available) {
        this.quantityBox.dataset.enabled = false;
        return;
      }

      this.quantityBox.dataset.enabled = true;

      // Sets the limit on the quantity box input
      if (Shopify.theme_settings.limit_quantity && inventory_policy === 'deny') {
        this.quantityBox.querySelector('input').setAttribute('max', inventory_quantity);
      } else {
        this.quantityBox.querySelector('input').removeAttribute('max');
      }

      this.productQuantityBox.refreshQuantity();
    };

    updateInventoryRemainingText();
    updateQuantityBox();
  }

  _onSwitchVariant(data) {
    const { currentVariant } = data;

    if (this.variantsUI.isInIndeterminateState) {
      return;
    }

    this.formArea.classList.remove('product-form--error-option-unselected');
    this.productDetailsContainer.classList.remove('product-form--error-option-unselected');

    this.surfacePickUp.load(currentVariant.id);

    // Update Variant Information
    this._updatePrice(currentVariant);
    this._updateSKU(currentVariant);
    this._updateInventoryRemaining(currentVariant);
    this.onVariantChange(currentVariant);
  }
}
